import React, { useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import LoadingBar from "react-top-loading-bar";
import { toastError } from "../../../../shared/helpers";

import ActionPageHero from "../../../molecules/ActionPageHero";

import Stepper from "../../../molecules/Stepper/Stepper";
import CreateServiceSuccessModal from "../../../organisms/CreateServiceSuccessModal";
import AddProgramFormOne from "../../../templates/AddProgramForms/form.one";
import AddProgramFormThree from "../../../templates/AddProgramForms/form.three";
import AddProgramFormTwo from "../../../templates/AddProgramForms/form.two";

const AddProgramPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const loader = useRef(null);

  const [step, setStep] = useState(1);
  const [fields, setFields] = useState({});
  const [submitted, setSubmitted] = useState(false);

  const updateFields = (data) => setFields({ ...fields, ...data });

  const discardData = () => {
    navigate(`/dashboard/organization/${id}/programs`);
  };

  // Utility functions
  const formatEligibilities = (eligibilities) => {
    if (!eligibilities?.length) return "";
    return eligibilities.length > 1
      ? Object.values(eligibilities).reduce(
          (prev, curr) => (prev.value ? prev.value : prev) + ", " + curr.value,
        )
      : eligibilities[0].value;
  };

  const processGroupData = (array, groupKey) => {
    const groupInputs = array.filter(([key]) => key.includes(groupKey));
    if (groupInputs.length === 0) return {};

    return groupInputs.reduce((group, input) => {
      const key = input[0].split("-")[1];
      const value = input[1];

      if (key === "languages") {
        const languages =
          value.length > 1
            ? value.reduce(
                (prev, curr) => (prev.value ? prev.value : prev) + ", " + curr.value,
              )
            : value[0].value;
        return { ...group, languages: [languages] };
      }

      return {
        ...group,
        [key]: value.value ?? value,
      };
    }, {});
  };

  const submitForm = (data, mode = "publish") => {
    // Process data arrays
    const dataArray = Object.entries({ ...fields, ...data });

    const eligibilities = formatEligibilities(fields?.eligibilities);
    const contact = processGroupData(dataArray, "contact");
    const location = processGroupData(dataArray, "location");
    const phone = processGroupData(dataArray, "phone");
    const schedule = data.schedule;

    updateFields(data);

    const formData = {
      orgId: id,
      name: fields.name,
      status: fields.status,
      alternateName: fields.alternateName,
      description: fields.description,
      url: fields.url,
      email: fields.email,
      interpretationServices: fields.interpretationServices,
      applicationProcess: fields.applicationProcess,
      waitTime: fields.waitTime,
      cost: fields.cost,
      accreditations: fields.accreditations,
      licenses: fields.licenses,
      opCategory: fields.opCategory?.value?.toLowerCase(),
      subOpCategory: fields.subOpCategory?.value,
      requiredDocuments: fields.requiredDocuments,
      paymentsAccepted: fields.paymentsAccepted,
      eligibilities,
      fundings: fields.fundings,
      serviceAreas: fields.serviceAreas,
      languages: fields.languages.value,
      programId: fields.programId,
      autoScreen: fields.autoScreen,
      location: { ...location, schedule },
      phone,
      contact,

      isPublished: mode === "publish",
      draft: false,
    };

    if (fields.overallCapacity)
      formData.overallCapacity = Number(fields.overallCapacity);
    if (fields.currentlyServing)
      formData.currentlyServing = Number(fields.currentlyServing);

    toastError("Cannot perform this action on Demo mode");
  };

  return (
    <main className="flex flex-col w-full relative">
      <LoadingBar height={2} color="#00AF72" ref={loader} />
      <div className="flex justify-between items-center">
        <ActionPageHero
          className="organization-container"
          title="Add Service"
          description="Create new service"
          back
          goBackText="Back to All Services"
        />
      </div>
      <section className="relative">
        <div className="w-2/5 mx-auto mt-10 mb-6">
          <Stepper
            activeStep={step}
            disableClick
            steps={[
              {
                value: 1,
              },
              {
                value: 2,
              },
              {
                value: 3,
              },
            ]}
            changeStep={setStep}
          />
        </div>
        <div className="">
          {step === 1 && (
            <AddProgramFormOne
              changeStep={setStep}
              fields={fields}
              updateFields={updateFields}
              saveToDraft={(data) => submitForm(data, "draft")}
              discard={discardData}
            />
          )}
          {step === 2 && (
            <AddProgramFormTwo
              changeStep={setStep}
              fields={fields}
              updateFields={(data) => updateFields(data)}
              saveToDraft={(data) => submitForm(data, "draft")}
              discard={discardData}
            />
          )}
          {step === 3 && (
            <AddProgramFormThree
              changeStep={setStep}
              fields={fields}
              saveToDraft={(data) => submitForm(data, "draft")}
              updateFields={(data) => submitForm(data)}
              discard={discardData}
            />
          )}
        </div>
      </section>
      {submitted && (
        <CreateServiceSuccessModal
          closeModal={() =>
            (window.location.pathname = `/dashboard/organization/${id}/programs`)
          }
        />
      )}
    </main>
  );
};

export default AddProgramPage;
